import { useEffect, useRef, useState } from 'react';

export interface Option {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Show a filter box inside the panel (for long option lists). */
  searchable?: boolean;
}

/**
 * A compact none-or-more multi-select: a button that opens a checkbox popover.
 * Closes on outside click / Escape. Selecting multiple values OR-s them.
 */
export default function MultiSelect({ label, options, selected, onChange, searchable }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const sel = new Set(selected);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const shown = searchable && query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = (value: string) => {
    const next = new Set(sel);
    next.has(value) ? next.delete(value) : next.add(value);
    onChange([...next]);
  };

  return (
    <div className={`ms${open ? ' ms-open' : ''}`} ref={ref}>
      <button type="button" className="ms-summary" onClick={() => setOpen(o => !o)}>
        {label}
        {selected.length > 0 && <span className="ms-count">{selected.length}</span>}
      </button>
      {open && (
        <div className="ms-panel">
          {searchable && (
            <input
              type="search"
              className="ms-search"
              placeholder={`Filter ${label.toLowerCase()}…`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          )}
          {selected.length > 0 && (
            <button type="button" className="ms-clear" onClick={() => onChange([])}>
              Clear {label.toLowerCase()}
            </button>
          )}
          <div className="ms-options">
            {shown.map(o => (
              <label key={o.value} className="ms-option">
                <input type="checkbox" checked={sel.has(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
            {shown.length === 0 && <span className="muted ms-empty">No matches</span>}
          </div>
        </div>
      )}
    </div>
  );
}
